/**
 * `__djgg*` field-directive sentinels — the shared structural GUARDS every
 * DocStore adapter recognizes writes by.
 *
 * The sentinel TYPES live next door in `./ports/storage.ts`; the factories that
 * CREATE the values are adapter port methods (`serverTimestamp()` /
 * `increment()` / `arrayUnion()`) and the writers' `fieldValues`. This module
 * owns only recognizing them, so no adapter can disagree about their shape.
 *
 * Each directive is resolved by the backend AGAINST THE STORED VALUE (Postgres
 * expresses all four in the `ON CONFLICT DO UPDATE` clause), which is what makes
 * them race-free where a read-modify-write would not be.
 */
import type {
  FieldArrayUnion,
  FieldIncrement,
  FieldMax,
  ServerTimestampSentinel,
} from './ports/storage.js';

/** Any of the four field-op directives. */
export type FieldSentinel =
  | ServerTimestampSentinel
  | FieldIncrement
  | FieldArrayUnion
  | FieldMax;

/** True when `v` is the server-timestamp sentinel. */
export function isServerTimestampSentinel(v: unknown): v is ServerTimestampSentinel {
  return typeof v === 'object' && v !== null && '__djggServerTimestamp' in v;
}

/** True when `v` is an increment directive. */
export function isFieldIncrement(v: unknown): v is FieldIncrement {
  return typeof v === 'object' && v !== null && '__djggIncrement' in v;
}

/** True when `v` is an array-union directive. */
export function isFieldArrayUnion(v: unknown): v is FieldArrayUnion {
  return typeof v === 'object' && v !== null && '__djggArrayUnion' in v;
}

/** True when `v` is a running-maximum directive. */
export function isFieldMax(v: unknown): v is FieldMax {
  return typeof v === 'object' && v !== null && '__djggMax' in v;
}

/** True when `v` is any {@link FieldSentinel} (so a plain-value merge or a fake
 *  interpreter can branch on "directive vs literal" with one call). */
export function isFieldSentinel(v: unknown): v is FieldSentinel {
  return (
    isServerTimestampSentinel(v) ||
    isFieldIncrement(v) ||
    isFieldArrayUnion(v) ||
    isFieldMax(v)
  );
}

